import { type InputHTMLAttributes, forwardRef } from "react";

export type StandardTextFieldProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "size"
> & {
  /** Design: error row uses border #ff4347 and helper below. */
  errorMessage?: string;
};

const inputShell =
  "relative h-[46px] w-full rounded-[10px] border border-solid bg-white outline-none transition-[color,box-shadow] font-medium text-[14px] leading-normal";

/**
 * Figma: StandardDesign · node 8839:3866 "Component 122" (Text box - standard variants).
 * Placeholder / filled / error colors match design tokens from the file.
 */
export const CommonTextInput = forwardRef<
  HTMLInputElement,
  StandardTextFieldProps
>(function StandardTextField(
  { errorMessage, className, value, disabled, id: idProp, ...rest },
  ref,
) {
  const hasValue = value != null && String(value).length > 0;
  const borderClass = errorMessage
    ? "border-[#ff4347]"
    : hasValue
      ? "border-[rgba(28,25,22,0.8)]"
      : "border-[rgba(28,25,22,0.2)]";

  return (
    <div
      className={
        errorMessage
          ? "flex w-full flex-col items-start gap-2"
          : "relative h-[46px] w-full"
      }
    >
      <input
        ref={ref}
        id={idProp}
        disabled={disabled}
        value={value}
        className={[
          inputShell,
          "w-full px-[23px]",
          "placeholder:text-[rgba(28,25,22,0.2)]",
          borderClass,
          "text-[rgba(28,25,22,0.8)]",
          "focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0",
          "disabled:cursor-not-allowed disabled:opacity-60",
          !errorMessage && "absolute inset-0",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        {...rest}
      />
      {errorMessage ? (
        <div className="flex w-full items-start justify-end px-1">
          <p className="min-w-0 flex-1 overflow-hidden text-ellipsis text-left text-[12px] font-normal leading-4 text-[#ff4347]">
            {errorMessage}
          </p>
        </div>
      ) : null}
    </div>
  );
});

CommonTextInput.displayName = "CommonTextInput";
